import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Drawer,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBusOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptOutlined";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/config";
import { usePlanStatus } from "../hooks/usePlanStatus";
import { useQueryClient } from "@tanstack/react-query";
import BookOnlineIcon from "@mui/icons-material/BookOnlineOutlined";
import { PROJECT_TERMINOLOGY } from "../project/project_module/enums";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStationOutlined";
import {
  OperationType,
  tabType,
  UserPermissions,
  UserScope,
} from "../types/operationType";
import AccountBalanceIcon from "@mui/icons-material/AccountBalanceOutlined";
import { handleLogout } from "../utils/utils";
import { SidebarVersion } from "./VersionDisplay";
import BuildIcon from "@mui/icons-material/BuildOutlined";
import WorkIcon from "@mui/icons-material/WorkOutlined";

// Base menu items that are common across all operation types
const baseMenuItems = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    key: "dashboard",
  },
];

// Common menu items that appear in most configurations
const getCommonMenuItems = (permissions: UserPermissions | undefined) => [
  {
    text: "Fuel Reports",
    icon: <LocalGasStationIcon />,
    path: "/fuel-reports",
    key: "fuel-reports",
  },
  {
    text: "R & M Reports",
    icon: <BuildIcon />,
    path: "/r-m-reports",
    key: "r-m-reports",
  },
  {
    text: "Job Reports",
    icon: <WorkIcon />,
    path: "/job-reports",
    key: "job-reports",
  },
  {
    text: "Company",
    icon: <BusinessIcon />,
    path: getCompanyProfilePath(permissions),
    key: "profile",
  },
];

// Conditional menu items based on operation type
const conditionalMenuItems = {
  project: {
    text: PROJECT_TERMINOLOGY.SINGULAR,
    icon: <DescriptionIcon />,
    path: `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
    key: PROJECT_TERMINOLOGY.LOWER_PLURAL,
  },
  superProject: {
    text: PROJECT_TERMINOLOGY.SINGULAR,
    icon: <DescriptionIcon />,
    path: `/super`,
    key: PROJECT_TERMINOLOGY.LOWER_PLURAL,
  },
  booking: {
    text: "Tour Booking",
    icon: <BookOnlineIcon />,
    path: "/rental",
    key: "booking",
  },
  dailyService: {
    text: "Daily Service",
    icon: <DirectionsBusIcon />,
    path: "/trips",
    key: "trips",
  },
};

// Function to check if user has permission for a specific module
export const checkModuleAccess = (
  permissions: UserPermissions | undefined,
  module: string
): boolean => {
  if (!permissions) return false;

  switch (module) {
    case "DASHBOARD":
      return permissions.canViewDashboard;
    case "TRIP":
      return permissions.canViewTrips;
    case "VEHICLE_EXPENSE":
      return permissions.canViewVehicleExpense;
    case "OTHER_EXPENSE":
      return permissions.canViewOtherExpense;
    case "REPORTS":
      return (
        permissions.canViewTripPNL ||
        permissions.canViewTripLedger ||
        permissions.canViewIncomeReport ||
        permissions.canViewExpenseReport ||
        permissions.canViewFuelReport ||
        permissions.canViewMaintenanceReport ||
        permissions.canViewCommissionReport ||
        permissions.canViewPickupReport ||
        permissions.canViewDriverCleanerReport ||
        permissions.canViewReportByVehicles
      );
    case "FUEL_REPORTS":
      return permissions.canViewFuelReport;
    case "MAINTENANCE_REPORTS":
      return permissions.canViewMaintenanceReport;
    case "COMPANY":
      return permissions.canViewCompany;
    case "USER":
      return permissions.canViewUsers;
    case "VEHICLE":
      return permissions.canViewVehicles;
    case "VEHICLE_FINANCE":
      return permissions.canViewVehicleFinance;
    case "VEHICLE_MAINTENANCE":
      return permissions.canViewVehicleMaintenance;
    case "VEHICLE_DOCUMENT":
      return permissions.canViewVehicleDocument;
    case "VEHICLE_INSURANCE":
      return permissions.canViewVehicleInsurance;
    case "VEHICLE_PERMIT":
      return permissions.canViewVehiclePermit;
    case "VEHICLE_ODOMETER":
      return permissions.canViewVehicleOdometer;
    case "MASTER_DATA":
      return (
        permissions.canViewDriver ||
        permissions.canViewCleaner ||
        permissions.canViewLadyAttendant ||
        permissions.canViewRoute ||
        permissions.canViewServiceOperator ||
        permissions.canViewParty ||
        permissions.canViewBranch ||
        permissions.canViewPickupMan ||
        permissions.canViewPetrolPump ||
        permissions.canViewParkingLocation ||
        permissions.canViewGarage ||
        permissions.canViewMechanic
      );
    case "BOOKING":
      return permissions.canViewBooking;
    case "CONTRACT":
      return permissions.canViewContract;
    default:
      return false;
  }
};

// Function to check if user has permission for any of the specified modules
export const checkAnyModuleAccess = (
  permissions: UserPermissions | undefined,
  modules: string[]
): boolean => {
  if (!permissions) return false;
  return modules.some((module) => checkModuleAccess(permissions, module));
};

// Function to get the appropriate company profile path based on available permissions
const getCompanyProfilePath = (
  permissions: UserPermissions | undefined
): string => {
  if (!permissions) return "/profile";

  // Check permissions in order of preference
  if (checkModuleAccess(permissions, tabType.COMPANY)) {
    return "/profile";
  }
  if (checkModuleAccess(permissions, tabType.USER)) {
    return "/company-staff";
  }
  if (checkModuleAccess(permissions, tabType.VEHICLE)) {
    return "/vehicles";
  }
  if (checkModuleAccess(permissions, tabType.MASTER_DATA)) {
    return "/master-data";
  }

  return "/profile"; // fallback
};

// Function to build menu items based on operation type and permissions
const buildMenuItems = (
  operationType: string,
  permissions: UserPermissions | undefined,
  userScope: UserScope,
  blockNavigation: (path: string) => boolean
) => {
  // For project scope users, only show project item
  if (userScope === UserScope.PROJECT) {
    return [conditionalMenuItems.project];
  }

  const menuItems = [];

  // 1. Dashboard (always show)
  menuItems.push(...baseMenuItems);

  // 2. Operation-specific items (based on operation type)
  switch (operationType?.toLowerCase()) {
    case OperationType.LARGE_FLEET_ALL.toLowerCase():
      menuItems.push(conditionalMenuItems.superProject);
      break;
    default:
      menuItems.push(conditionalMenuItems.dailyService);
      break;
  }

  // 3. Common menu items (show all without permission checks)
  const commonItems = getCommonMenuItems(permissions);

  // Vehicle Expense
  const vehicleExpenseItem = commonItems.find(
    (item) => item.key === "vehicle-expense"
  );
  if (vehicleExpenseItem) menuItems.push(vehicleExpenseItem);

  // Other Expense
  const otherExpenseItem = commonItems.find(
    (item) => item.key === "other-expense"
  );
  if (otherExpenseItem) menuItems.push(otherExpenseItem);

  // Finance and Reports (these items don't exist in commonItems, removing them)

  // Fuel Reports
  const fuelReportsItem = commonItems.find((item) => item.key === "fuel-reports");
  if (fuelReportsItem) menuItems.push(fuelReportsItem);

  // R & M Reports
  const rmReportsItem = commonItems.find((item) => item.key === "r-m-reports");
  if (rmReportsItem) menuItems.push(rmReportsItem);

  // Job Reports (now always show)
  const jobReportsItem = commonItems.find((item) => item.key === "job-reports");
  if (jobReportsItem) menuItems.push(jobReportsItem);

  // Company
  const companyItem = commonItems.find((item) => item.key === "profile");
  if (companyItem) menuItems.push(companyItem);

  return menuItems;
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  sidebarKey?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  isMobile,
  sidebarKey,
}) => {
  const navigate = useNavigate();
  const { planStatus, blockNavigation, loading } = usePlanStatus();
  const queryClient = useQueryClient();

  // Get current menu items based on plan type
  // If planStatus is loading, show no items to prevent flashing
  const currentMenuItems = React.useMemo(() => {
    if (loading) return []; // Show no items while loading
    return buildMenuItems(
      planStatus.company?.operationType || "default",
      planStatus.permissions,
      planStatus?.user?.userRole?.scope,
      blockNavigation
    );
  }, [
    loading,
    planStatus.company?.operationType,
    planStatus.permissions,
    planStatus?.user?.userRole?.scope,
    blockNavigation,
  ]);

  const SidebarItem = ({
    item,
    isLogout = false,
  }: {
    item: any;
    isLogout?: boolean;
  }) => (
    <ListItem
      button
      onClick={() => {
        if (isLogout) {
          handleLogout(queryClient);
        } else {
          navigate(item.path);
          if (isMobile) onClose();
        }
      }}
      sx={{
        flexDirection: "row",
        alignItems: "center",
        py: 1,
        px: 2,
        backgroundColor:
          !isLogout && sidebarKey === item.key ? "#f5f5f5" : "transparent",
        "&:hover": {
          backgroundColor: isLogout ? "error.lighter" : "primary.lighter",
        },
        borderLeft: !isLogout && sidebarKey === item.key ? 4 : 0,
        borderColor: "primary.main",
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: "auto",
          mr: 0.5,
          color: isLogout
            ? "error.main"
            : sidebarKey === item.key
              ? "primary.main"
              : "text.secondary",
          "& .MuiSvgIcon-root": {
            fontSize: "0.85rem",
          },
        }}
      >
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.text}
        primaryTypographyProps={{
          variant: "body2",
          sx: {
            fontSize: "0.75rem",
            fontWeight: sidebarKey === item.key ? 700 : 600,
            color: isLogout
              ? "error.main"
              : sidebarKey === item.key
                ? "primary.main"
                : "text.secondary",
            textTransform: "none",
            letterSpacing: "0.2px",
            lineHeight: "1.4",
          },
        }}
      />
    </ListItem>
  );

  const sidebarContent = (
    <Box
      sx={{
        marginTop: "0px",
        height: "100%",
        display: "flex",
        paddingTop: "10px",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <List sx={{ width: "100%", pt: 0 }}>
          {currentMenuItems.map((item) => (
            <SidebarItem key={item?.key} item={item} />
          ))}
        </List>

        <Box>
          <Divider />
          <List sx={{ width: "100%", pt: 0.5, pb: 0.5 }}>
            <SidebarItem
              item={{
                text: "Logout",
                icon: <LogoutIcon />,
                path: "",
              }}
              isLogout={true}
            />
          </List>
        </Box>
      </Box>
      <Box sx={{ p: 0, textAlign: "center", borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <SidebarVersion />
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={isOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "165px",
            mt: "0px",
            height: "100%",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        width: isOpen ? "165px" : 0,
        flexShrink: 0,
        transition: "width 0.2s ease",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "165px",
          height: "calc(100vh - 64px)",
          position: "fixed",
          top: "64px",
          left: isOpen ? 0 : -240,
          transition: "left 0.2s ease",
          borderRight: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          zIndex: 1000,
          bgcolor: "background.default",
        }}
      >
        {sidebarContent}
      </Paper>
    </Box>
  );
};

export default Sidebar;
